/* Revenue headline: the total both ways (gross and net, always), the change vs the
   previous period, and ONE split bar — by Revenue type, or by Seller. In the
   Seller view the part not sold in chat is its own neutral share, so the bar still
   adds up to Revenue (CONTEXT.md "Not sold in chat"). */

import { useTranslation } from 'react-i18next';
import type { StatisticsMoney, StatSeller } from '../../../api/client';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { Icon, Seg } from '../../ChatAssistant/compact/kit';
import { HATCH, SELLER_COLOR, pick, share, typeColor, typeOrder, usd, type MoneyMode } from './model';

export type SplitAxis = 'type' | 'seller';

interface Item {
  key: string;
  label: string;
  value: number;
  color: string;
  hatch?: boolean;
  off?: boolean;
  tip?: string;
  onClick?: () => void;
}

interface Props {
  money: StatisticsMoney | null;
  mode: MoneyMode;
  days: number;
  axis: SplitAxis;
  onAxis: (a: SplitAxis) => void;
  hasCrm: boolean;
  onOpenTab: (tab: 'ai' | 'team') => void;
}

export function RevenueWidget({ money, mode, days, axis, onAxis, hasCrm, onOpenTab }: Props) {
  const { t } = useTranslation('statsUnified');
  const other: MoneyMode = mode === 'gross' ? 'net' : 'gross';
  const total = pick(money?.revenue, mode);
  const prev = pick(money?.previous, mode);
  const change = prev > 0 ? ((total - prev) / prev) * 100 : null;

  const items: Item[] = !money
    ? []
    : axis === 'type'
      ? [...money.revenue.by_type]
          .sort((a, b) => typeOrder(a.type) - typeOrder(b.type))
          .map((x) => ({ key: x.type, label: t(`types.${x.type}`, { defaultValue: x.type }), value: x[mode], color: typeColor(x.type) }))
      : (['ai', 'team', 'unknown', 'not_in_chat'] as StatSeller[]).map((k) => {
          const off = k === 'team' && !hasCrm;
          return {
            key: k,
            label: t(`sellers.${k}`),
            value: off ? 0 : pick(money.sellers[k], mode),
            color: off ? 'var(--om-hover)' : SELLER_COLOR[k],
            hatch: off || k === 'not_in_chat',
            off,
            tip: off ? t('sellers.teamNoCrm') : t(`sellers.${k}Tip`),
            onClick: !off && (k === 'ai' || k === 'team') ? () => onOpenTab(k) : undefined,
          };
        });
  const sum = items.reduce((a, x) => a + x.value, 0) || 1;

  return (
    <div className="stx-revenue" style={{ display: 'grid', gridTemplateColumns: '220px minmax(0,1fr)', gap: 28, alignItems: 'center', padding: '16px 18px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>{t('revenue.title')}</span>
        <span style={{ font: f(700, 28, 1.15), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{money ? usd(total) : '—'}</span>
        <span style={{ font: f(500, 12), color: C.sub, fontVariantNumeric: 'tabular-nums' }}>
          {money ? t(`revenue.${other}Line`, { value: usd(pick(money.revenue, other)) }) : ''}
        </span>
        {change !== null && (
          <span style={{ font: f(500, 11.5), color: C.purpleLt, whiteSpace: 'nowrap' }}>
            {`${change >= 0 ? '▲' : '▼'} ${Math.abs(change).toFixed(0)}% `}{t('revenue.vsPrevious', { count: days })}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ font: f(600, 11.5), color: C.sub, whiteSpace: 'nowrap', flex: 1 }}>{axis === 'type' ? t('revenue.whatSold') : t('revenue.whoSold')}</span>
          <Seg size="sm" value={axis} onChange={onAxis} options={[{ id: 'type', label: t('revenue.byType') }, { id: 'seller', label: t('revenue.bySeller') }]} />
        </div>
        <div style={{ display: 'flex', gap: 2, height: 10, borderRadius: 99, overflow: 'hidden', background: C.divider }}>
          {items.filter((x) => x.value > 0 || x.off).map((x) => (
            <span key={x.key} title={`${x.label} · ${x.off ? '—' : usd(x.value)}`} style={{ height: '100%', width: `${x.off ? 6 : (x.value / sum) * 100}%`, background: x.color, backgroundImage: x.hatch ? HATCH : undefined }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: '2px 4px', flexWrap: 'wrap', marginLeft: -8 }}>
          {items.map((x) => (
            <button
              key={x.key}
              type="button"
              title={x.tip}
              onClick={x.onClick}
              className={x.onClick ? 'cpt-hover-row' : undefined}
              style={{ height: 26, padding: '0 8px', display: 'flex', alignItems: 'center', gap: 7, border: 0, borderRadius: 6, background: 'transparent', color: 'var(--om-body)', font: f(500, 12.5), cursor: x.onClick ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
            >
              <span style={{ width: 9, height: 9, borderRadius: 3, background: x.color, backgroundImage: x.hatch ? HATCH : undefined }} />
              {x.label}
              <span style={{ font: f(600, 12.5), color: x.off ? C.muted : C.text, fontVariantNumeric: 'tabular-nums' }}>{x.off ? t('sellers.noCrm') : usd(x.value)}</span>
              {!x.off && <span style={{ color: C.muted }}>{share(x.value, sum)}%</span>}
              {x.onClick && <Icon name="chevron-right" size={12} color={C.muted} />}
            </button>
          ))}
          {money && !items.length && <span style={{ padding: '0 8px', font: f(400, 12), color: C.muted }}>{t('revenue.empty')}</span>}
        </div>
      </div>
    </div>
  );
}
